import * as Immutable from 'immutable';
import * as React from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Button, Grid } from 'semantic-ui-react';

import analytics from '../../../shared/services/analytics/analytics';
import { setUserSettings } from '../../../actions/AuthenticationActions';
import { setActiveTabLanguage } from '../../../actions/EditorActions/EditorActions';
import { addFlashMessagePromise } from '../../../actions/FlashMessagesActions';
import { changeSimulationStatus } from '../../../actions/Simulation/DataAction';
import { ErrorBoundary } from '../../../containers/ErrorBoundary';
import ErrorCatch from '../../../containers/ErrorBoundary/ErrorCatch';
import { localeData } from '../../../locales/data';
import { StateType } from '../../../reducers';
import { getUserIdSelector, hasVisualizationPermission } from '../../../reducers/AuthenticationReducer/AuthenticationSelector';
import { EditorTab } from '../../../reducers/EditorReducer/EditorReducer.types';

import {
  activeTabIndexSelector,
  componentActivationSelector,
  childTabsSelector,
  currentChildActiveTabSelector,
  currentLanguageSelector,
  decaySelector,
  delaySelector,
  editorOptionsSelector,
  getTabsSelector,
  instrumentTypeSelector,
  maxTradeSelector,
  maxPositionSelector,
  maxTruncationSelector,
  minDecaySelector,
  minTruncationSelector,
  nanHandlingSelector,
  neutralizationSelector,
  pasteurizationSelector,
  regionSelector,
  selectionHandlingSelector,
  selectionLimitSelector,
  testPeriodSelector,
  truncationSelector,
  unitHandlingSelector,
  universeSelector,
} from '../../../reducers/EditorReducer/EditorSelector';
import { tabsStatusSelector } from '../../../reducers/GenericSelectors';
import { alphaDetailSimulationSelector } from '../../../reducers/SimulationReducer/DataReducer/DataSelector';
import { checkSettingsWithoutCode, intlFormatMessage, isSimulationEdited } from '../../../shared/helpers';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { isdeviceUpToTablet } from '../../../shared/helpers/responsive';
import { AlphaStage, DispatchConnect } from '../../../types';

import './EditorSettingsForm.scss';
import SettingsChoices from './EditorSettingsForms/SettingsChoices';
import SettingsInput from './EditorSettingsForms/SettingsInput';
import './EditorSettingsFormElement.scss';
import SettingsISOPeriodInput from './EditorSettingsForms/SettingsISOPeriodInput';
import moment from 'moment';

interface EditorSettingsFormStateProps {
  alphaDetails: any;
  currentActiveTab: number;
  currentChildActiveTab: number;
  currentLanguage: string;
  currentMultiTabs: Immutable.List<EditorTab>;
  currentTabs: Immutable.List<EditorTab>;
  decay: any;
  delay: string;
  instrumentType: string;
  isConsultant: boolean;
  maxTruncation?: number;
  minDecay?: number;
  minTruncation?: number;
  nanHandling: string;
  neutralization: string;
  options?: any;
  pasteurization: string;
  region: string;
  selectionHandling: string;
  selectionLimit: any;
  tabsStatus: Immutable.List<any>;
  componentActivation: AlphaStage;
  testPeriod: any;
  truncation: any;
  unitHandling: string;
  universe: string;
  userId: string;
  maxTrade: string;
  maxPosition: string;
}

export interface Setting {
  value: string;
  hasError: boolean;
  max?: number;
  min?: number;
}

interface EditorSettings {
  language: Setting;
  instrumentType: Setting;
  region: Setting;
  universe: Setting;
  selectionLimit: Setting;
  selectionHandling: Setting;
  delay: Setting;
  neutralization: Setting;
  decay: Setting;
  testPeriod: Setting;
  truncation: Setting;
  pasteurization: Setting;
  unitHandling: Setting;
  nanHandling: Setting;
  componentActivation: Setting;
  maxTrade: Setting;
}

interface EditorSettingsFormState {
  settings: EditorSettings;
}

interface EditorSettingsFormProps {
  handleClose: () => void;
}

interface EditorSettingsFormStateDispatchers {
  dispatchSettingsUpdate: (settings: any, currentActiveTab: number, childActiveTab: number) => void;
  dispatchFlashMessageError: (message: Immutable.List<string>) => Promise<any>;
  dispatchSetUserSettings: (data: any, userId: string) => Promise<any>;
  dispatchChangeSimulationStatus: (tabIndex: number, currentStatus: string, childIndex?: number, nextStatus?: string) => void;
}

const SubtitleOfSettings = ({ name }: any) => (
  <div className="code-editor-settings__form-header">
    {name} &nbsp;
    <FormattedMessageHelper tagName="span" id="editor-settings-subtitle" defaultMessage={localeData.en['editorSettingsForm.settings']} />
  </div>
);

type EditorSettingsGenericType = InjectedIntlProps &
  EditorSettingsFormStateProps &
  EditorSettingsFormStateDispatchers &
  EditorSettingsFormProps;

export class EditorSettingsForm extends React.Component<EditorSettingsGenericType, EditorSettingsFormState> {
  constructor(props: EditorSettingsGenericType) {
    super(props);
    this.state = {
      settings: {
        language: { value: props.currentLanguage, hasError: false },
        instrumentType: { value: props.instrumentType, hasError: false },
        region: { value: props.region, hasError: false },
        universe: { value: props.universe, hasError: false },
        selectionLimit: props.selectionLimit && props.selectionLimit.toJS(),
        selectionHandling: { value: props.selectionHandling, hasError: false },
        delay: { value: props.delay, hasError: false },
        neutralization: { value: props.neutralization, hasError: false },
        decay: props.decay.toJS(),
        truncation: props.truncation.toJS(),
        pasteurization: { value: props.pasteurization, hasError: false },
        unitHandling: { value: props.unitHandling, hasError: false },
        nanHandling: { value: props.nanHandling, hasError: false },
        componentActivation: { value: props.componentActivation, hasError: false },
        testPeriod: props.testPeriod?.toJS?.() ?? {
          value: moment.duration({ years: 0, months: 0, days: 0 }).toISOString(),
          hasError: false,
        },
        ...(this.props.options.get('maxTrade') && {
          maxTrade: { value: props.maxTrade ?? null, hasError: false },
        }),
        ...(this.props.options.get('maxPosition') && {
          maxPosition: { value: props.maxPosition ?? null, hasError: false },
        }),
        ...(this.props.options.get('lookback') && {
          lookback: {
            value: props.currentTabs.getIn([props.currentActiveTab, 'settings', 'lookback', 'value']) ?? null,
            hasError: false,
          },
        }),
      },
    };
  }

  checkIfSettingsHaveErrors = () =>
    Object.keys(this.state.settings).filter((setting: string) => (this as any).state.settings[setting].hasError).length > 0;

  checkIfRegionIsInvalid = (instrumentType: string, region: string) => {
    const regionPath = ['choices', 'instrumentType', instrumentType, 'region', region];
    const instrumentTypePath = ['choices', 'instrumentType', instrumentType];
    Object.keys(this.props.options.toJS()).map((setting: any) => {
      const regionOptions = this.props.options.getIn([setting].concat(regionPath));
      const instrumentTypeOptions = this.props.options.getIn([setting].concat(instrumentTypePath));
      if (regionOptions) {
        const optionValid = regionOptions.filter((option: any) => option.get('value') === (this as any).state.settings[setting].value);
        const newValue = regionOptions.getIn([0, 'value']);
        if (optionValid.size < 1) {
          this.setState((state: any) => ({
            settings: {
              ...state.settings,
              [setting]: {
                ...state.settings[setting],
                value: newValue,
              },
            },
          }));
        }
      } else if (instrumentTypeOptions) {
        const optionValid = instrumentTypeOptions.filter((option: any) => option.get('value') === (this as any).props[setting]);
        const newValue = instrumentTypeOptions.getIn([0, 'value']);
        if (optionValid.size < 1) {
          this.setState((state: any) => ({
            settings: {
              ...state.settings,
              [setting]: {
                ...state.settings[setting],
                value: newValue,
              },
            },
          }));
        }
      }
    });
  };

  checkIfInstrumentTypeIsInvalid = (instrumentType: string) => {
    const instrumentTypePath = ['region', 'choices', 'instrumentType', instrumentType];
    const regionOptions = this.props.options.getIn(instrumentTypePath);
    const optionValid = regionOptions.filter((option: any) => option.get('label') === this.state.settings.region);
    const newValue = regionOptions.getIn([0, 'label']);
    if (optionValid.size < 1) {
      this.setState(
        (state: any) => ({
          settings: {
            ...state.settings,
            region: {
              ...state.settings.region,
              value: newValue,
            },
          },
        }),
        () => this.checkIfRegionIsInvalid(instrumentType, newValue)
      );
    }
  };

  updateOtherSettings = (name: string, value: any) => {
    const instrumentType = this.state.settings.instrumentType.value;
    if (name === 'region') {
      this.checkIfRegionIsInvalid(instrumentType, value);
    }
    if (name === 'instrumentType') {
      this.checkIfInstrumentTypeIsInvalid(value);
    }
  };
  checkInputHasError = (settingName: string, settingValue: any) => {
    if (settingName === 'testPeriod') {
      const duration = moment.duration(settingValue).asMilliseconds();
      const minDuration = moment.duration((this as any).state.settings[settingName].min).asMilliseconds();
      const maxDuration = moment.duration((this as any).state.settings[settingName].max).asMilliseconds();

      const isValid = duration >= minDuration && duration <= maxDuration;

      if (isValid) {
        this.setState(
          (state: any) => ({
            settings: {
              ...state.settings,
              [settingName]: {
                ...state.settings[settingName],
                hasError: false,
              },
            },
          }),
          () => this.updateOtherSettings(settingName, settingValue)
        );
      } else {
        this.setState(
          (state: any) => ({
            settings: {
              ...state.settings,
              [settingName]: {
                ...state.settings[settingName],
                hasError: true,
              },
            },
          }),
          () => this.updateOtherSettings(settingName, settingValue)
        );
      }
    }

    const settingsToCheck: any = ['decay', 'truncation', 'selectionLimit'];

    if (settingsToCheck.includes(settingName)) {
      const min = (this as any).state.settings[settingName].min;
      const max = (this as any).state.settings[settingName].max;
      const parsedValue = parseFloat(settingValue);
      const required = this.props.options.getIn([settingName, 'required']);
      if (
        (min !== undefined && min !== null && parsedValue < min) ||
        (max !== undefined && max !== null && parsedValue > max) ||
        (isNaN(parsedValue) && required)
      ) {
        this.setState(
          (state: any) => ({
            settings: {
              ...state.settings,
              [settingName]: {
                ...state.settings[settingName],
                hasError: true,
              },
            },
          }),
          () => this.updateOtherSettings(settingName, settingValue)
        );
      } else if ((this as any).state.settings[settingName].hasError) {
        this.setState(
          (state: any) => ({
            settings: {
              ...state.settings,
              [settingName]: {
                ...state.settings[settingName],
                hasError: false,
              },
            },
          }),
          () => this.updateOtherSettings(settingName, settingValue)
        );
      } else {
        this.updateOtherSettings(settingName, settingValue);
      }
    } else {
      this.updateOtherSettings(settingName, settingValue);
    }
  };

  private updateError({ settingName, settingValue, hasError }: { settingName: string; settingValue: any; hasError: boolean }) {
    this.setState(
      (state: any) => ({
        settings: {
          ...state.settings,
          [settingName]: {
            ...state.settings[settingName],
            hasError: true,
          },
        },
      }),
      () => this.updateOtherSettings(settingName, settingValue)
    );
  }

  onChangeHandler = (event: any, data: any) => {
    const target = event.target;
    const value = data.value;
    const name = target.name ? target.name : data.name;
    analytics.trackCustomEventOnce('sim_change_setting', { event_label: name });
    this.setState(
      (state: any) => ({
        settings: {
          ...state.settings,
          [name]: {
            ...state.settings[name],
            value,
          },
        },
      }),
      () => this.checkInputHasError(name, value)
    );
  };

  onChangeHandlerPortal = (name: string, value: any) => {
    analytics.trackCustomEventOnce('sim_change_setting', { event_label: name });
    this.setState(
      (state: any) => ({
        settings: {
          ...state.settings,
          [name]: {
            ...state.settings[name],
            value,
          },
        },
      }),
      () => this.checkInputHasError(name, value)
    );
  };

  handlerSaveSettings = () => {
    analytics.trackCustomEvent('sim_click_save_settings');
    const errors = checkSettingsWithoutCode((this as any).props.currentTabs.get(this.props.currentActiveTab), this.props.options);

    if (errors.size > 0) {
      this.props
        // @ts-ignore
        .dispatchFlashMessageError(Immutable.fromJS(errors))
        .then(() => {
          this.adjustSettingsHeight();
        })
        .catch();
    } else {
      this.props
        .dispatchSetUserSettings(this.getSettingsData(), this.props.userId)
        .then(() => {
          this.adjustSettingsHeight();
        })
        .catch();
    }
  };

  adjustSettingsHeight = () => {
    const popup: any = document.querySelector('.editor-top-bar-left__settings-popup');
    const editorCode: any = document.querySelector('.editor-code');
    const editorBarLeft: any = document.querySelector('.editor-top-bar-left');
    const settingsButton = document.querySelector('.editor-top-bar-left__settings-btn');

    if (isdeviceUpToTablet() && settingsButton) {
      const top = settingsButton.getBoundingClientRect().bottom;
      popup.style.top = `${top}px`;
    } else if (editorCode && popup && editorBarLeft) {
      const popupStyle = getComputedStyle ? getComputedStyle(popup, null) : popup.currentStyle;
      const editorBarLeftStyle = getComputedStyle ? getComputedStyle(editorBarLeft, null) : editorBarLeft.currentStyle;
      const offsetTop: number = editorCode.offsetTop;
      const marginTop: number = parseFloat(popupStyle.marginTop) || 0;
      const editorBarLeftHalf: number = (parseFloat(editorBarLeftStyle.height) || 0) / 2;
      const top = offsetTop + editorBarLeftHalf + marginTop;
      popup.style.top = `${top}px`;
    }
  };
  getSettingsData = () => {
    const settingsToSend: any = Object.assign({}, this.state.settings);
    Object.keys(this.state.settings).map((setting: string) => {
      if (this.props.options.getIn([setting, 'type']) === 'integer' || setting === 'delay') {
        settingsToSend[setting] = parseInt(settingsToSend[setting].value, 10);
      } else if (this.props.options.getIn([setting, 'type']) === 'float') {
        settingsToSend[setting] = parseFloat(settingsToSend[setting].value);
      } else {
        settingsToSend[setting] = settingsToSend[setting].value;
      }
    });
    settingsToSend.visualization = false;

    return settingsToSend;
  };

  handleClose = () => {
    const currentActiveTab = this.props.currentActiveTab;
    const currentTab = (this as any).props.currentTabs.get(currentActiveTab);
    const childActiveTab = currentTab.get('childTabs') !== undefined ? this.props.currentChildActiveTab : -1;
    const currentCode = currentTab.get('superAlpha')
      ? { combo: currentTab.get('combo'), selection: currentTab.get('selection') }
      : childActiveTab > -1
      ? currentTab.getIn(['childTabs', childActiveTab, 'code'])
      : currentTab.get('code');
    const tabStatus =
      childActiveTab > -1
        ? this.props.tabsStatus.getIn([currentActiveTab, 'childTabs', childActiveTab, 'currentStatus'])
        : this.props.tabsStatus.getIn([currentActiveTab, 'parentTab', 'currentStatus']);
    if (isSimulationEdited(currentCode, this.state.settings, this.props.alphaDetails)) {
      // @ts-ignore
      this.props.dispatchChangeSimulationStatus(currentActiveTab, tabStatus, childActiveTab);
    } else {
      const tabStatusAfterSimulating =
        childActiveTab > -1
          ? this.props.tabsStatus.getIn([currentActiveTab, 'childTabs', childActiveTab, 'statusAfterSimulation'])
          : this.props.tabsStatus.getIn([currentActiveTab, 'parentTab', 'statusAfterSimulation']);
      // @ts-ignore
      this.props.dispatchChangeSimulationStatus(currentActiveTab, tabStatus, childActiveTab, tabStatusAfterSimulating);
    }
    this.props.dispatchSettingsUpdate(this.state.settings, currentActiveTab, childActiveTab);
    this.props.handleClose();
  };

  getSimulationSettings = () => {
    const { currentTabs, currentActiveTab, currentChildActiveTab }: EditorSettingsFormStateProps = this.props;
    const activeTab: any = currentTabs.get(currentActiveTab);
    const currentInstrumentType = this.state.settings.instrumentType.value;
    const region = this.state.settings.region.value;
    const currentChildTab = this.props.currentTabs.getIn([currentActiveTab, 'childTabs', currentChildActiveTab]);
    const subtitle =
      // @ts-ignore
      currentChildTab && currentChildTab.get('tabName')
        ? // @ts-ignore
          currentChildTab.get('tabName')
        : intlFormatMessage('combo.settings.subtitle', this.props.intl);

    return [
      { name: activeTab && activeTab.get('tabName'), component: SubtitleOfSettings, order: -1, shouldntDisplay: ['regular'], width: 16 },
      { name: 'language', path: [], component: SettingsChoices, order: 0, width: 8, shouldntDisplay: [] },
      { name: 'instrumentType', path: [], component: SettingsChoices, order: 1, width: 8, shouldntDisplay: [] },
      {
        name: 'region',
        path: ['instrumentType', currentInstrumentType],
        component: SettingsChoices,
        order: 2,
        width: 4,
        shouldntDisplay: [],
      },
      {
        name: 'universe',
        path: ['instrumentType', currentInstrumentType, 'region', region],
        component: SettingsChoices,
        order: 7,
        width: 8,
        shouldntDisplay: [],
      },
      {
        name: subtitle,
        component: SubtitleOfSettings,
        order: 6,
        shouldntDisplay: ['regular'],
        width: 16,
      },
      {
        name: 'delay',
        path: ['instrumentType', currentInstrumentType, 'region', region],
        component: SettingsChoices,
        order: 3,
        width: 4,
        modifiers: ['code-editor-settings__form-element--half'],
        shouldntDisplay: [],
      },
      {
        name: 'selectionHandling',
        path: [],
        component: SettingsChoices,
        order: 4,
        width: 4,
        shouldntDisplay: ['regular', 'multiSimulation'],
      },
      {
        name: 'selectionLimit',
        path: [],
        component: SettingsInput,
        order: 5,
        width: 4,
        shouldntDisplay: ['regular', 'multiSimulation'],
      },
      {
        name: 'neutralization',
        path: ['instrumentType', currentInstrumentType, 'region', region],
        component: SettingsChoices,
        order: 8,
        width: 8,
        shouldntDisplay: [],
      },
      { name: 'decay', path: [], component: SettingsInput, order: 9, width: 4, shouldntDisplay: [] },
      { name: 'truncation', path: [], component: SettingsInput, order: 10, width: 4, shouldntDisplay: [] },
      { name: 'pasteurization', path: [], component: SettingsChoices, order: 11, width: 4, shouldntDisplay: [] },
      this.state.settings.language.value !== 'PYTHON' && {
        name: 'unitHandling',
        path: [],
        component: SettingsChoices,
        order: 12,
        width: 4,
        shouldntDisplay: [],
      },
      this.state.settings.language.value !== 'PYTHON' && {
        name: 'nanHandling',
        path: [],
        component: SettingsChoices,
        order: 13,
        width: 4,
        shouldntDisplay: [],
      },
      this.props.options.get('lookback') &&
        this.state.settings.language.value === 'PYTHON' && {
          name: 'lookback',
          path: [],
          component: SettingsInput,
          order: 0.5,
          width: 4,
          shouldntDisplay: ['multiSimulation'],
        },

      this.props.options.get('testPeriod') && {
        name: 'testPeriod',
        path: [],
        component: SettingsISOPeriodInput,
        order: 14,
        width: 4,
        shouldntDisplay: [],
      },
      this.props.options.get('componentActivation') && {
        name: 'componentActivation',
        path: [],
        component: SettingsChoices,
        order: 15,
        width: 4,
        shouldntDisplay: ['regular', 'multiSimulation'],
      },
      this.props.options.get('maxTrade') && {
        name: 'maxTrade',
        path: [],
        component: SettingsChoices,
        order: 16,
        width: 4,
        shouldntDisplay: [],
      },
      this.props.options.get('maxPosition') && {
        name: 'maxPosition',
        path: [],
        component: SettingsChoices,
        order: 17,
        width: 4,
        shouldntDisplay: [],
      },
    ].filter(Boolean);
  };

  render() {
    const { currentTabs, currentActiveTab }: EditorSettingsFormStateProps = this.props;
    try {
      const disableButtons = this.checkIfSettingsHaveErrors();
      const activeTab: any = currentTabs.get(currentActiveTab);
      const childTabs = activeTab.get('childTabs');
      let simulationSettingsOrder: any = this.getSimulationSettings();
      if (childTabs) {
        simulationSettingsOrder = simulationSettingsOrder
          .filter((setting: any) => !setting.shouldntDisplay.includes('multiSimulation'))
          .sort((a: any, b: any) => a.order - b.order);
      } else if (currentTabs.getIn([currentActiveTab, 'superAlpha'])) {
        simulationSettingsOrder = simulationSettingsOrder
          .filter((setting: any) => !setting.shouldntDisplay.includes('superAlpha'))
          .sort((a: any, b: any) => a.order - b.order);
      } else {
        simulationSettingsOrder = simulationSettingsOrder.filter((setting: any) => !setting.shouldntDisplay.includes('regular'));
      }

      return (
        <ErrorBoundary showUI={true} isNotFullScreen={true} isModal={true}>
          <div>
            <Button className="code-editor-settings__close--button" onClick={this.props.handleClose} />
            <Grid className="simulate__settings">
              {simulationSettingsOrder.map((setting: any) => {
                const SettingComponent = setting.component;
                return (
                  <Grid.Column mobile={8} computer={setting.width} key={setting.name}>
                    <SettingComponent
                      name={setting.name}
                      onChangeHandler={this.onChangeHandler}
                      selection={true}
                      pathOptions={setting.path}
                      modifiers={setting.modifiers ? setting.modifiers : []}
                      currentValue={(this as any).state.settings[setting.name]}
                      language={this.state.settings.language.value}
                      onChangeHandlerPortal={this.onChangeHandlerPortal}
                      hasChildTabs={!!childTabs}
                      isSuperAlpha={!!currentTabs.getIn([currentActiveTab, 'superAlpha'])}
                    />
                  </Grid.Column>
                );
              })}
              <Grid.Column mobile={16} computer={16}>
                <div className="code-editor-settings__form-div">
                  <Button disabled={disableButtons} className="button--primary button--hsx" onClick={this.handlerSaveSettings}>
                    <FormattedMessageHelper id="editorSettingsForm.saveSettings" />
                  </Button>
                  <Button disabled={disableButtons} className="button--lg" onClick={this.handleClose}>
                    <FormattedMessageHelper
                      tagName="span"
                      id="editorSettingsForm.apply"
                      defaultMessage={localeData.en['editorSettingsForm.apply']}
                    />
                  </Button>
                </div>
              </Grid.Column>
            </Grid>
          </div>
        </ErrorBoundary>
      );
    } catch (error) {
      return <ErrorCatch error={error} />;
    }
  }
}

const mapStateToProps = (state: StateType): EditorSettingsFormStateProps => ({
  // @ts-ignore
  currentTabs: getTabsSelector(state),
  currentMultiTabs: childTabsSelector(state) || Immutable.List(),
  currentChildActiveTab: currentChildActiveTabSelector(state),
  // @ts-ignore
  currentActiveTab: activeTabIndexSelector(state),
  region: regionSelector(state),
  instrumentType: instrumentTypeSelector(state),
  universe: universeSelector(state),
  selectionLimit: selectionLimitSelector(state),
  selectionHandling: selectionHandlingSelector(state),
  delay: delaySelector(state),
  decay: decaySelector(state),
  neutralization: neutralizationSelector(state),
  truncation: truncationSelector(state),
  // @ts-ignore
  maxTruncation: maxTruncationSelector(state),
  // @ts-ignore
  minTruncation: minTruncationSelector(state),
  // @ts-ignore
  minDecay: minDecaySelector(state),
  options: editorOptionsSelector(state),
  currentLanguage: currentLanguageSelector(state),
  isConsultant: hasVisualizationPermission(state),
  // @ts-ignore
  userId: getUserIdSelector(state),
  pasteurization: pasteurizationSelector(state),
  unitHandling: unitHandlingSelector(state),
  testPeriod: testPeriodSelector(state),
  nanHandling: nanHandlingSelector(state),
  alphaDetails: alphaDetailSimulationSelector(state),
  tabsStatus: tabsStatusSelector(state),
  componentActivation: componentActivationSelector(state),
  maxTrade: maxTradeSelector(state),
  maxPosition: maxPositionSelector(state),
});

function mapDispatchToProps(dispatch: Dispatch<any>): EditorSettingsFormStateDispatchers {
  return {
    dispatchSettingsUpdate: async (settings: any, activeTabIndex: number, childActiveTab: number) =>
      dispatch(setActiveTabLanguage(settings, activeTabIndex, childActiveTab)),
    dispatchFlashMessageError: async (message: Immutable.List<string>) =>
      dispatch(addFlashMessagePromise(message, { context: 'generic', intl: false })),
    dispatchSetUserSettings: async (data: any, userId: string) => dispatch(setUserSettings(data, userId)),
    dispatchChangeSimulationStatus: (tabIndex: number, currentStatus: string, childIndex?: number, nextStatus?: string) =>
      dispatch(changeSimulationStatus(tabIndex, currentStatus, childIndex, nextStatus)),
  };
}

export default injectIntl(
  connect<EditorSettingsFormStateProps, DispatchConnect<EditorSettingsFormStateDispatchers>, any, StateType>(
    mapStateToProps,
    mapDispatchToProps
  )(EditorSettingsForm)
);
